// Considered: DialogWrapper (the prior implementation's shell) — rejected. `CustomDialog`
// portals to `document.body`, which puts every datum outside the composer's `relationships`
// container and makes the parity walker judge the sheet as uncontained. The contract's own
// shape is a scrim + sheet rendered in place (`rels > <RelationshipSheet> > dialog`), which
// is the shape `AspectAttachDialogOrganism` already established for the attach dialog —
// reused here verbatim, including its scrim-has-no-keyboard-role reasoning.
//
// Considered: a bespoke target picker for "What can be on the other end" — rejected. The
// prototype's `<SphereScopeEditor>` IS the unified filter surface used for aspect and
// context applicability, and the platform's equivalent is `ApplicabilityEditorMolecule`.
// The scope section composes that molecule and stores its answer in `target_filter` via
// `deriveApplicability` / `buildApplicability`, so a target rule authored here reads the
// same way it does everywhere else.
//
// Vocabulary note: the prototype's REL_KINDS are Reference / Temporal / Roll-up. The
// platform's `SchemaRelationship['relationship_type']` union is `custom | temporal |
// dependency` — so Reference maps to `custom`, Temporal to `temporal`, and the third
// option is the platform's `dependency` rather than the prototype's Roll-up, which has no
// stored value to write. Adding a roll-up kind is a schema-service change, not a rendering
// choice; it is out of this spec's scope.
import React, { useEffect, useMemo, useState } from 'react';
import { useDialogFocus } from '../../../hooks/useDialogFocus';
import { useTranslation } from 'localization';
import type { SchemaRelationship } from 'types/schema';
import type { UnifiedFilters } from 'types/filters';
import type { OrgAvailableTypeSchema } from 'types/schema';
import InputAtom from '../../atoms/Input/InputAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl';
import ApplicabilityEditorMolecule, {
  type ApplicabilityEditorInitialState,
  type ApplicabilityEditorParity,
  type ApplicabilityMode,
} from '../ApplicabilityEditor/ApplicabilityEditorMolecule';
import { readRelationshipScope } from './relationshipScope';
import type { PropertyOption } from '../PropertyFilter/PropertyFilterMolecule';
import type { SchemaSource } from '../SphereTypeSelect';
import { buildApplicability } from '../../../utils/contextFlowSerialization';
import { relationshipScopeCopy } from './relationshipScopeCopy';
import InlineConfirmMolecule from './InlineConfirmMolecule';
import './RelationshipSheetMolecule.scss';

const KEY = 'studio.typeEditor.relationships';

/**
 * The contract ids for the scope section. `ApplicabilityEditorMolecule` and the picker it
 * composes are mounted by the context editor too, whose contract names none of this — so the
 * ids belong here, with the decomposition that declares them, and are passed in.
 *
 * Mode-vocabulary note: the design's strip has four modes (Any sphere / Families / Types /
 * Custom) to the platform's three — families and types are one *Specific types* picker here,
 * which offers both axes at once. That is a schema-side vocabulary difference, not a
 * rendering choice, and it is recorded in the plan rather than forked into a second editor.
 */
const SCOPE_PARITY: ApplicabilityEditorParity = {
  modeOption: 'rels.opt-label',
  familyHint: 'rels.familyhint',
  unmapped: 'rels.unmapped-join',
  legacyMode: 'rels.custom',
  done: 'rels.done',
  typeSelect: {
    trigger: 'rels.change',
    chipRemove: 'rels.button#3',
    clear: 'rels.clear',
    search: 'rels.input',
    searchClear: 'rels.button#4',
    familyOption: 'rels.types',
    familyGroup: 'rels.button#5',
    typeOption: 'rels.button#6',
  },
  /**
   * The condition catalog's scope banner — the editor's built-in catalog, which the custom
   * tree's drill-in opens (the sheet wires no page-level catalog).
   */
  catalog: {
    searchingCount: 'rels.ef-org-type-count#2',
    scopedCount: 'rels.type#2',
    coreScope: 'rels.core#2',
  },
};

/** The kinds that have a stored value. See the vocabulary note at the top of this file. */
const REL_KINDS: SchemaRelationship['relationship_type'][] = ['custom', 'temporal', 'dependency'];

/** Contract order (`type-relationship.jsx:21-26`), not the type union's order. */
const REL_CARDS: SchemaRelationship['cardinality'][] = [
  'one_to_one',
  'many_to_one',
  'one_to_many',
  'many_to_many',
];

/** The subset of a relationship this sheet authors. */
export type RelationshipSheetPatch = Pick<
  SchemaRelationship,
  'name' | 'description' | 'reverse_label' | 'relationship_type' | 'cardinality' | 'required' | 'target_filter'
>;

export interface RelationshipSheetMoleculeProps {
  visible: boolean;
  /** `null` = authoring a brand-new relationship; otherwise the row being edited. */
  relationship: SchemaRelationship | null;
  /** The declaring type's display name — the head's breadcrumb names it. */
  typeName: string;
  /**
   * Renders as the rail's current selection body (no scrim, no `role="dialog"`/`aria-modal`, no
   * own focus trap — the host, `InspectorRailOrganism`, already owns Escape and containment
   * non-modally, the same convention `NewAspectDialogMolecule.railMode` established in EAINT-933
   * Phase 3.2). Default `false` keeps the standalone-modal shape for any host that has not moved
   * to the rail (e.g. `RelationshipOverrideSheetMolecule`'s own `rels.dialog#3`/`#4`, which have
   * no surface rebinding recorded yet).
   */
  railMode?: boolean;
  onHide: () => void;
  onSave: (patch: RelationshipSheetPatch) => void;
  /**
   * Removes the relationship outright. Only ever supplied for an own, non-inherited
   * relationship: an inherited one "can't be removed by one" — that promise is the
   * ancestor's to keep, so the composer never passes this to an override sheet. Omitted
   * while authoring a brand-new relationship, which has nothing to remove yet. The
   * prototype's own `RelationshipSheet` (`type-relationship.jsx:81`) has no remove
   * affordance at all, so this control carries no contract `data-datum` — EAINT-933
   * Phase 3, Current Priority item 2.
   */
  onRemove?: () => void;
  /** Catalogs for the scope editor. Supplied by the composer, which owns the hooks. */
  familyTypeSchemas?: OrgAvailableTypeSchema[];
  availableProperties?: PropertyOption[];
  sources?: Record<string, SchemaSource>;
  approachSources?: Record<string, string[]>;
  /**
   * Seeds the scope editor's interaction state (open picker, typed query, open drill-in) so a
   * story or a test can declare one of those readings without driving the clicks that reach
   * it. The composer never passes it.
   */
  initialScopeState?: ApplicabilityEditorInitialState;
  /**
   * Overrides the mode `deriveApplicability` reads out of the stored rule. The mode strip is
   * editor state, not part of the rule: switching it from Custom to a type list keeps the
   * conditions in state while `buildApplicability` writes only the list — which is exactly
   * what the *Carried over from the old definition* note is about. No stored rule can express
   * that pairing (a rule carrying conditions reads back as `custom`), so a story or a test
   * that needs the note's second line declares the mode here. The composer never passes it.
   */
  initialScopeMode?: ApplicabilityMode;
  className?: string;
  /**
   * Fires whenever "has the author changed something worth losing" changes, compared against
   * what the sheet loaded (EAINT-933 Phase 3.3: "dirty draft never lost silently", extended from
   * `NewAspectDialogMolecule.onDirtyChange` to the sheets whose editor lives behind a rail
   * portal rather than being mounted by the composer directly). A host that closes the rail
   * selection without checking this discards whatever was typed or changed with no warning.
   */
  onDirtyChange?: (dirty: boolean) => void;
}

const RelationshipSheetMolecule: React.FC<RelationshipSheetMoleculeProps> = ({
  visible,
  relationship,
  typeName,
  railMode = false,
  onHide,
  onSave,
  onRemove,
  familyTypeSchemas = [],
  availableProperties = [],
  sources,
  approachSources,
  initialScopeState,
  initialScopeMode,
  className,
  onDirtyChange,
}) => {
  const { t } = useTranslation();
  const isNew = relationship === null;
  const titleId = 'relationship-sheet-title';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [reverse, setReverse] = useState('');
  const [kind, setKind] = useState<SchemaRelationship['relationship_type']>('custom');
  const [cardinality, setCardinality] = useState<SchemaRelationship['cardinality']>('many_to_one');
  const [required, setRequired] = useState(false);
  const [scope, setScope] = useState(() => readRelationshipScope(relationship?.target_filter, initialScopeMode));
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  /** What the sheet loaded for this open — dirtiness is a comparison against THIS, not against
   * empty, since editing an existing relationship starts from real values (unlike the "start"
   * kind's blank draft). Reset in the same effect that (re)seeds the fields below. */
  const initialRef = React.useRef({
    name: relationship?.name ?? '',
    description: relationship?.description ?? '',
    reverse: relationship?.reverse_label ?? '',
    kind: relationship?.relationship_type ?? 'custom',
    cardinality: relationship?.cardinality ?? 'many_to_one',
    required: !!relationship?.required,
    scope: readRelationshipScope(relationship?.target_filter, initialScopeMode),
  });

  useEffect(() => {
    const seeded = {
      name: relationship?.name ?? '',
      description: relationship?.description ?? '',
      reverse: relationship?.reverse_label ?? '',
      kind: relationship?.relationship_type ?? ('custom' as SchemaRelationship['relationship_type']),
      cardinality: relationship?.cardinality ?? ('many_to_one' as SchemaRelationship['cardinality']),
      required: !!relationship?.required,
      scope: readRelationshipScope(relationship?.target_filter, initialScopeMode),
    };
    initialRef.current = seeded;
    setName(seeded.name);
    setDescription(seeded.description);
    setReverse(seeded.reverse);
    setKind(seeded.kind);
    setCardinality(seeded.cardinality);
    setRequired(seeded.required);
    setScope(seeded.scope);
    setConfirmingRemove(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, relationship?.system_name]);

  const isDirty =
    name !== initialRef.current.name ||
    description !== initialRef.current.description ||
    reverse !== initialRef.current.reverse ||
    kind !== initialRef.current.kind ||
    cardinality !== initialRef.current.cardinality ||
    required !== initialRef.current.required ||
    JSON.stringify(scope) !== JSON.stringify(initialRef.current.scope);
  useEffect(() => {
    onDirtyChange?.(isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  /*
    The two things that hold Save, stated rather than implied: the contract's
    `problems` list (`type-relationship.jsx:96-99`). `scopeIncomplete` is a chosen
    "specific types" rule with nothing chosen — a rule that would offer the picker
    nothing, which is a different answer from "any sphere".
  */
  const scopeIncomplete = scope.mode === 'specific' && scope.families.length + scope.types.length === 0;
  const problems = useMemo(() => {
    const rows: string[] = [];
    if (!name.trim()) rows.push(t(`${KEY}.problemName`));
    if (scopeIncomplete) rows.push(t(`${KEY}.problemScope`));
    return rows;
  }, [name, scopeIncomplete, t]);

  const dialogRef = React.useRef<HTMLDivElement>(null);
  // Rail mode: an expanded selection inside the rail, not a modal — the rail's own
  // `useDialogFocus` (`trapFocus: false`) already manages focus and Escape at its container,
  // same as `NewAspectDialogMolecule`'s `side > start` mount.
  useDialogFocus(visible, dialogRef, railMode ? { onDismiss: onHide, trapFocus: false, manageFocus: false } : { onDismiss: onHide });

  if (!visible) return null;

  const submit = () =>
    onSave({
      name: name.trim(),
      description,
      reverse_label: reverse,
      relationship_type: kind,
      cardinality,
      required,
      target_filter: buildApplicability({
        applicabilityMode: scope.mode,
        applicabilityFamilies: scope.families,
        applicabilityTypes: scope.types,
        applicabilityFilter: scope.filter,
      }) ?? {},
    });

  const field = (
    datumLabel: string,
    datumHint: string,
    label: string,
    hint: string,
    control: React.ReactNode,
    optional?: boolean
  ) => (
    <div className="relationship-sheet__field">
      <span className="relationship-sheet__field-label" data-datum={datumLabel} data-kind="label">
        {label}
        {optional && <i className="relationship-sheet__optional">{t(`${KEY}.optional`)}</i>}
      </span>
      {control}
      <span className="relationship-sheet__field-hint" data-datum={datumHint} data-kind="hint">
        {hint}
      </span>
    </div>
  );

  const content = (
    <div
      className={['relationship-sheet', railMode ? className : null].filter(Boolean).join(' ')}
      ref={dialogRef}
      data-datum="rels.dialog#2"
      data-kind="dialog"
      data-testid="relationship-sheet"
      role={railMode ? undefined : 'dialog'}
      aria-modal={railMode ? undefined : 'true'}
      aria-labelledby={titleId}
      onClick={railMode ? undefined : (event) => event.stopPropagation()}
    >
        <div className="relationship-sheet__head">
          <b id={titleId} data-datum="rels.add-a-relationship#2" data-kind="strong">
            {isNew
              ? t(`${KEY}.addRelationship`)
              : t(`${KEY}.editRelationshipNamed`, { name: relationship?.name })}
          </b>
          <span className="relationship-sheet__crumb" data-datum="rels.declared-by" data-kind="breadcrumb">
            {t(`${KEY}.declaredByCrumb`, { typeName })}
          </span>
          {!isNew && onRemove && (
            <ButtonAtom
              variant="danger"
              size="small"
              label={t(`${KEY}.removeRelationship`)}
              onClick={() => setConfirmingRemove(true)}
              data-testid="relationship-sheet-remove"
            />
          )}
          <ButtonAtom
            variant="text"
            size="small"
            label={t(`${KEY}.cancel`)}
            onClick={onHide}
            data-datum="rels.cancel"
            data-kind="button"
            data-testid="relationship-sheet-cancel"
          />
          <ButtonAtom
            variant="primary"
            size="small"
            label={isNew ? t(`${KEY}.addRelationshipSubmit`) : t(`${KEY}.save`)}
            disabled={problems.length > 0}
            onClick={submit}
            data-datum="rels.add-relationship"
            data-kind="button"
            data-testid="relationship-sheet-save"
          />
          {/* A disabled Save states its reason rather than hiding it (ux-craft I2). */}
          {problems.length > 0 && (
            <span className="relationship-sheet__problems">
              <b
                className="relationship-sheet__problems-head"
                data-testid="relationship-sheet-problems-head"
              >
                {t(`${KEY}.problemsLeft`, { count: problems.length })}
              </b>
              {problems.map((problem) => (
                <span
                  key={problem}
                  className="relationship-sheet__problem"
                  data-testid="relationship-sheet-problem"
                >
                  {problem}
                </span>
              ))}
            </span>
          )}
        </div>

        {confirmingRemove && (
          <InlineConfirmMolecule
            tone="danger"
            testId="relationship-sheet-remove-confirm"
            titleId="relationship-sheet-remove-confirm-title"
            title={t(`${KEY}.removeConfirm.title`, { name: relationship?.name })}
            titleTestId="relationship-sheet-remove-confirm-title"
            body={
              relationship?.reverse_label
                ? t(`${KEY}.removeConfirm.body`, {
                    name: relationship?.name,
                    reverse: relationship?.reverse_label,
                  })
                : t(`${KEY}.removeConfirm.bodyNoReverse`, { name: relationship?.name })
            }
            bodyTestId="relationship-sheet-remove-confirm-body"
            actions={[
              {
                label: t(`${KEY}.removeConfirm.accept`),
                onClick: () => onRemove?.(),
                variant: 'danger',
                'data-testid': 'relationship-sheet-remove-confirm-accept',
              },
              {
                label: t(`${KEY}.removeConfirm.keepIt`),
                onClick: () => setConfirmingRemove(false),
                variant: 'text',
                'data-testid': 'relationship-sheet-remove-confirm-dismiss',
              },
            ]}
          />
        )}

        <div className="relationship-sheet__body">
          <h3 className="relationship-sheet__section" data-datum="rels.what-it-is" data-kind="heading">
            {t(`${KEY}.sectionWhatItIs`)}
          </h3>
          {field(
            'rels.label',
            'rels.hint',
            t(`${KEY}.nameLabel`),
            t(`${KEY}.nameHint`),
            <InputAtom
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t(`${KEY}.namePlaceholder`)}
              aria-label={t(`${KEY}.nameLabel`)}
              data-datum="rels.duplicate-of"
              data-kind="input"
              data-testid="relationship-sheet-name"
            />
          )}
          {field(
            'rels.label#2',
            'rels.hint#2',
            t(`${KEY}.descriptionLabel`),
            t(`${KEY}.descriptionHint`),
            <InputAtom
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t(`${KEY}.descriptionPlaceholder`)}
              aria-label={t(`${KEY}.descriptionLabel`)}
              data-datum="rels.marks-this-record-as-the-same-thing-as-a"
              data-kind="input"
              data-testid="relationship-sheet-description"
            />
          )}
          {field(
            'rels.label#3',
            'rels.hint#3',
            t(`${KEY}.reverseLabel`),
            t(`${KEY}.reverseHint`),
            <InputAtom
              value={reverse}
              onChange={(e) => setReverse(e.target.value)}
              placeholder={t(`${KEY}.reversePlaceholder`)}
              aria-label={t(`${KEY}.reverseLabel`)}
              data-datum="rels.duplicated-by"
              data-kind="input"
              data-testid="relationship-sheet-reverse"
            />,
            true
          )}

          <h3
            className="relationship-sheet__section"
            data-datum="rels.what-can-be-on-the-other-end"
            data-kind="heading"
          >
            {t(`${KEY}.sectionOtherEnd`)}
          </h3>
          <ApplicabilityEditorMolecule
            allowFilterGroups={false}
            layout="narrow"
            mode={scope.mode}
            families={scope.families}
            types={scope.types}
            filter={scope.filter}
            onModeChange={(mode) => setScope((s) => ({ ...s, mode }))}
            onFamiliesChange={(families) => setScope((s) => ({ ...s, families }))}
            onTypesChange={(types) => setScope((s) => ({ ...s, types }))}
            onFilterChange={(filter) => setScope((s) => ({ ...s, filter }))}
            availableProperties={availableProperties}
            familyTypeSchemas={familyTypeSchemas}
            sources={sources}
            approachSources={approachSources}
            parity={SCOPE_PARITY}
            initialState={initialScopeState}
            copy={relationshipScopeCopy(t)}
            lead={t(`${KEY}.scopeLead`)}
            anyNote={t(`${KEY}.scopeAnyNote`)}
          />
          <span className="relationship-sheet__footnote" data-datum="rels.footnote" data-kind="hint">
            {t(`${KEY}.scopeFootnote`)}
          </span>

          <h3 className="relationship-sheet__section" data-datum="rels.how-it-behaves" data-kind="heading">
            {t(`${KEY}.sectionHowItBehaves`)}
          </h3>
          {field(
            'rels.label#12',
            'rels.hint#4',
            t(`${KEY}.kindLabel`),
            t(`${KEY}.kindNote.${kind}`),
            <SegmentedControlAtom
              mode="single"
              renderAs="button"
              optionKind="button"
              size="small"
              value={kind}
              onChange={(value) => setKind(value as SchemaRelationship['relationship_type'])}
              ariaLabel={t(`${KEY}.kindLabel`)}
              data-testid="relationship-sheet-kind"
              options={REL_KINDS.map((value, index) => ({
                value,
                label: t(`${KEY}.kind.${value}`),
                ...(index === 0 ? { datum: 'rels.opt-label#8' } : {}),
              }))}
            />
          )}
          {field(
            'rels.label#13',
            'rels.hint#5',
            t(`${KEY}.howManyLabel`),
            t(`${KEY}.cardinalityNote.${cardinality}`),
            <div className="relationship-sheet__cards" role="radiogroup" aria-label={t(`${KEY}.howManyLabel`)}>
              {REL_CARDS.map((value, index) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={cardinality === value}
                  className={`relationship-sheet__card${cardinality === value ? ' relationship-sheet__card--on' : ''}`}
                  data-container="each(cards)"
                  onClick={() => setCardinality(value)}
                  data-testid={`relationship-sheet-cardinality-${value}`}
                  {...(index === 0 ? { 'data-datum': 'rels.button#37', 'data-kind': 'button' } : {})}
                >
                  <span className="relationship-sheet__card-title">{t(`${KEY}.cardinality.${value}`)}</span>
                  <span className="relationship-sheet__card-note">{t(`${KEY}.cardinalityNote.${value}`)}</span>
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            role="switch"
            aria-checked={required}
            className={`relationship-sheet__required${required ? ' relationship-sheet__required--on' : ''}`}
            onClick={() => setRequired((r) => !r)}
            data-datum="rels.button#38"
            data-kind="button"
            data-testid="relationship-sheet-required"
          >
            <i className={`pi ${required ? 'pi-check-square' : 'pi-stop'}`} aria-hidden="true" />
            <span>
              <span className="relationship-sheet__required-title">{t('studio.typeEditor.row.required')}</span>
              <span className="relationship-sheet__required-note">{t(`${KEY}.requiredNote`)}</span>
            </span>
          </button>
        </div>
    </div>
  );

  if (railMode) return content;

  return (
    /*
      The scrim closes on click, which is why it carries no keyboard role of its own:
      Cancel is the keyboard path, and a `role="button"` here would put an unlabelled
      control in the tab order in front of the sheet's own.
    */
    <div
      className={['relationship-sheet__scrim', className].filter(Boolean).join(' ')}
      data-datum="rels.dialog"
      data-kind="dialog"
      data-testid="relationship-sheet-scrim"
      role="presentation"
      onClick={onHide}
    >
      {content}
    </div>
  );
};

RelationshipSheetMolecule.displayName = 'RelationshipSheetMolecule';

export default RelationshipSheetMolecule;
